import React, { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { alpha, useTheme } from "@mui/material/styles";
import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";
import {
  SIDEBAR_FAB_DRAG_THRESHOLD,
  SIDEBAR_FAB_SIZE,
  SIDEBAR_FAB_STORAGE_KEY,
  clampSidebarFabTop,
  readSidebarFabRatio,
  sidebarFabDashBelow,
  sidebarFabRatioFromTop,
  sidebarFabTopFromRatio,
} from "./sidebarFabPosition";

function readStoredRatio() {
  try {
    return (
      readSidebarFabRatio(localStorage.getItem(SIDEBAR_FAB_STORAGE_KEY)) ?? 1
    );
  } catch {
    return 1;
  }
}

function useViewportHeight() {
  const [height, setHeight] = useState(() =>
    typeof window === "undefined" ? 800 : window.innerHeight,
  );

  useEffect(() => {
    const onResize = () => setHeight(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return height;
}

/**
 * Menu tab shown while the sidebar is minimized.
 * Stays on the screen edge and slides up or down so it can leave the
 * bottom corner when it covers a list or a button.
 */
export default function SidebarMenuFab({
  anchor = "left",
  logoSrc,
  openLabel,
  dashboardLabel,
  onOpenMenu,
  onOpenDashboard,
}) {
  const theme = useTheme();
  const viewportHeight = useViewportHeight();
  const [ratio, setRatio] = useState(readStoredRatio);
  const [dragTop, setDragTop] = useState(null);
  const [dashOpen, setDashOpen] = useState(false);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const hideTimerRef = useRef(null);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const openDash = useCallback(() => {
    if (dragRef.current?.moved) return;
    clearHideTimer();
    setDashOpen(true);
  }, [clearHideTimer]);

  const scheduleCloseDash = useCallback(() => {
    clearHideTimer();
    hideTimerRef.current = setTimeout(() => {
      setDashOpen(false);
      hideTimerRef.current = null;
    }, 180);
  }, [clearHideTimer]);

  useEffect(
    () => () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    },
    [],
  );

  const top =
    dragTop == null ? sidebarFabTopFromRatio(ratio, viewportHeight) : dragTop;
  const dashBelow = sidebarFabDashBelow(top);
  const onLeft = anchor !== "right";

  const onPointerDown = (event) => {
    if (event.button !== 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      originTop: rect.top,
      moved: false,
    };
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;
    const dy = event.clientY - drag.startY;
    if (!drag.moved && Math.abs(dy) < SIDEBAR_FAB_DRAG_THRESHOLD) return;
    if (!drag.moved) {
      drag.moved = true;
      clearHideTimer();
      setDashOpen(false);
    }
    setDragTop(clampSidebarFabTop(drag.originTop + dy, window.innerHeight));
  };

  const finishDrag = (event) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;
    dragRef.current = null;
    const canceled = event.type === "pointercancel";
    if (drag.moved) {
      if (!canceled) {
        const nextRatio = sidebarFabRatioFromTop(
          drag.originTop + (event.clientY - drag.startY),
          window.innerHeight,
        );
        setRatio(nextRatio);
        try {
          localStorage.setItem(SIDEBAR_FAB_STORAGE_KEY, String(nextRatio));
        } catch {
          /* private mode */
        }
      }
      setDragTop(null);
    } else if (!canceled) {
      onOpenMenu();
    }
    // The synthetic click would open the menu a second time, or reopen it after a drag.
    suppressClickRef.current = true;
    setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);
  };

  return (
    <Box
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
      onMouseEnter={openDash}
      onMouseLeave={scheduleCloseDash}
      onClick={(event) => {
        if (!suppressClickRef.current) return;
        suppressClickRef.current = false;
        event.preventDefault();
        event.stopPropagation();
      }}
      sx={{
        position: "fixed",
        top,
        ...(onLeft ? { left: 16 } : { right: 16 }),
        zIndex: theme.zIndex.drawer + 2,
        width: SIDEBAR_FAB_SIZE,
        height: SIDEBAR_FAB_SIZE,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "#00bbcd38",
        boxShadow: `0 6px 20px ${alpha(theme.palette.common.black, 0.18)}`,
        cursor: dragTop == null ? "grab" : "grabbing",
        touchAction: "none",
        userSelect: "none",
        transition: "transform 0.18s ease, box-shadow 0.18s ease",
        "&:hover": {
          transform: "scale(1.04)",
          bgcolor: "background.paper",
          boxShadow: `0 10px 28px ${alpha(theme.palette.common.black, 0.22)}`,
        },
      }}
    >
      <Tooltip
        title={openLabel}
        placement={onLeft ? "right" : "left"}
        disableHoverListener={dragTop != null}
      >
        <IconButton
          onClick={(event) => {
            if (suppressClickRef.current) {
              suppressClickRef.current = false;
              event.preventDefault();
              event.stopPropagation();
              return;
            }
            onOpenMenu();
          }}
          aria-label={openLabel}
          sx={{
            width: "100%",
            height: "100%",
            p: 0.75,
            borderRadius: 2,
            cursor: "inherit",
          }}
        >
          <Box
            component="img"
            src={logoSrc}
            alt="TON"
            draggable={false}
            sx={{
              width: 36,
              height: 36,
              display: "block",
              borderRadius: 1,
              pointerEvents: "none",
            }}
          />
        </IconButton>
      </Tooltip>

      <Tooltip title={dashboardLabel} placement={dashBelow ? "bottom" : "top"}>
        <IconButton
          className="matrix-fab-home"
          aria-label={dashboardLabel}
          onPointerDown={(event) => event.stopPropagation()}
          onMouseEnter={openDash}
          onMouseLeave={scheduleCloseDash}
          onClick={(event) => {
            event.stopPropagation();
            setDashOpen(false);
            onOpenDashboard();
          }}
          sx={{
            position: "absolute",
            left: 0,
            ...(dashBelow
              ? { top: "100%", mt: 0.75 }
              : { bottom: "100%", mb: 0.75 }),
            width: SIDEBAR_FAB_SIZE,
            height: SIDEBAR_FAB_SIZE,
            borderRadius: 2,
            opacity: dashOpen ? 1 : 0,
            transform: dashOpen
              ? "translateY(0) scale(1)"
              : dashBelow
                ? "translateY(-6px) scale(0.9)"
                : "translateY(6px) scale(0.9)",
            pointerEvents: dashOpen ? "auto" : "none",
            transition:
              "opacity 0.18s ease, transform 0.18s ease, background-color 0.18s ease",
            color: "primary.main",
            bgcolor: alpha(theme.palette.background.paper, 0.96),
            border: "1px solid",
            borderColor: alpha(theme.palette.primary.main, 0.4),
            boxShadow: `0 6px 16px ${alpha(theme.palette.common.black, 0.16)}`,
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.14),
            },
          }}
        >
          <SpaceDashboardOutlinedIcon sx={{ fontSize: 26 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
